import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { SystemModuleLicense } from "../../api/api-generated";
import {
  useCreateSystemModuleLicenseMutation,
  useFindAllSystemModuleLicensesByCompanyIdQuery,
  useRemoveSystemModuleLicenseMutation,
} from "../../api/api-hooks";
import { TrimmedCompany } from "../../api/api-types";
import ImportButton from "./ImportButton";

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

interface Props {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  company: TrimmedCompany;
}

const SystemModulesDialog = ({ dialogOpen, setDialogOpen, company }: Props) => {
  const { data: systemModulesForCompany } =
    useFindAllSystemModuleLicensesByCompanyIdQuery({ id: company.id });
  const [selectedSystemModules, setSelectedSystemModules] = useState<
    (Optional<SystemModuleLicense, "id" | "company" | "orderId"> | null)[]
  >([]);
  const [createModule] = useCreateSystemModuleLicenseMutation();
  const [deleteModule] = useRemoveSystemModuleLicenseMutation();

  useEffect(() => {
    setSelectedSystemModules(
      systemModulesForCompany ? systemModulesForCompany : []
    );
  }, [systemModulesForCompany]);

  const handleSubmit = async () => {
    if (systemModulesForCompany) {
      for await (const module of systemModulesForCompany) {
        await deleteModule({ id: module.id });
      }
    }
    for await (const module of selectedSystemModules) {
      if (module) await createModule({ createSystemModuleLicenseDto: module });
    }
    setDialogOpen(false);
  };

  const handler = (event: any, idx: number, tag: string) => {
    if (tag === "newModule") {
      setSelectedSystemModules((state) => [...state, null]);
      return;
    }
    if (tag === "deleteModule") {
      setSelectedSystemModules((state) =>
        state.slice(0, idx).concat(state.slice(idx + 1))
      );
      return;
    }
    const modulesArrayCopy = [...selectedSystemModules];
    const moduleToUpdate = selectedSystemModules[idx];
    const updatedModule = {
      module: moduleToUpdate ? moduleToUpdate.module : "",
      license: moduleToUpdate ? moduleToUpdate.license : "",
      iv: moduleToUpdate ? moduleToUpdate.iv : "",
      companyId: company.id,
    };
    if (tag === "module") updatedModule.module = event.target.value;
    if (tag === "license") updatedModule.license = event.target.value;
    if (tag === "iv") updatedModule.iv = event.target.value;
    modulesArrayCopy[idx] = updatedModule;
    setSelectedSystemModules(modulesArrayCopy);
  };
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={dialogOpen}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle>Manage System Modules</DialogTitle>
      <DialogContent>
        <Grid container item xs direction="column" spacing={1}>
          <Grid item sx={{ mb: ".5rem" }}>
            <Typography>
              System-Level Modules for {company.companyName}
            </Typography>
          </Grid>
          <Grid container item spacing={1}>
            {selectedSystemModules.map((item, idx) => (
              <Grid
                item
                container
                spacing={1}
                alignItems="center"
                key={idx}
                style={{ marginLeft: "1.5rem" }}
              >
                <Grid item style={{ minWidth: "200px" }}>
                  <Select
                    name="module"
                    value={item?.module}
                    variant="outlined"
                    displayEmpty={true}
                    fullWidth
                    renderValue={(value) => item?.module}
                    onChange={(e) => handler(e, idx, "module")}
                  >
                    <MenuItem value="ADVANCED_HISTORY">
                      ADVANCED_HISTORY
                    </MenuItem>
                    <MenuItem value="WEATHER">WEATHER</MenuItem>
                    <MenuItem value="SWITCHES">SWITCHES</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs>
                  <TextField
                    name="license"
                    value={item?.license}
                    onChange={(e) => handler(e, idx, "license")}
                    label="License"
                    fullWidth
                    variant="outlined"
                  ></TextField>
                </Grid>
                <Grid item xs>
                  <TextField
                    name="iv"
                    fullWidth
                    value={item?.iv}
                    onChange={(e) => handler(e, idx, "iv")}
                    label="IV"
                    variant="outlined"
                  ></TextField>
                </Grid>
                <Grid item>
                  <IconButton
                    aria-label="Remove module"
                    onClick={(event) => handler(event, idx, "deleteModule")}
                    size="large"
                  >
                    <DeleteOutline color="error" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid item container alignItems="center">
            <Grid item style={{ marginLeft: "3rem" }}>
              <IconButton
                aria-label="Add module"
                onClick={(event: React.MouseEvent) => {
                  handler(event, -1, "newModule");
                }}
                size="large"
              >
                <AddCircleOutline color="primary" />
              </IconButton>
            </Grid>
            <Grid item>
              <ImportButton
                company={company}
                setSelectedSystemModules={setSelectedSystemModules}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          disabled={selectedSystemModules.length === 0}
        >
          Submit
        </Button>
        <Button
          onClick={() => {
            setDialogOpen(false);
            setSelectedSystemModules([]);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemModulesDialog;
